import React, { useRef, useState } from 'react';
import type { TabsProps } from 'antd';
import { Tabs } from 'antd';
import { useDrag, useDrop } from 'react-dnd';

const type = 'DraggableTabNode';
interface DraggableTabPaneProps extends React.HTMLAttributes<HTMLDivElement> {
  index: React.Key;
  moveNode: (dragIndex: React.Key, hoverIndex: React.Key) => void;
}

const DraggableTabNode = ({ index, children, moveNode }: DraggableTabPaneProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isOver }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
      };
    },
    drop: (item: { index: React.Key }) => {
      moveNode(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  // Style
  const style: React.CSSProperties = { marginRight: 2 };
  if (isOver) {
    style.transition = 'all 0.3s';
  }

  return (
    <div ref={ref} style={style}>
      {children}
    </div>
  );
};

const DraggableTabs = (props: any) => {
  const {
    items = [],
    params = {}, // update sort 所需要用到的參數object
  }: any = props;
  const [order, setOrder] = useState<React.Key[]>([]);
  const {
    sortParamName, // sortKey的欄位名稱
    updateSortFunction,
    hasUpdatePermission = true,
  } = params;

  const moveTabNode = (dragKey: React.Key, hoverKey: React.Key) => {
    if (!hasUpdatePermission) {
      return;
    }

    const newOrder = order.slice();

    items.forEach((item: any) => {
      if (item.key && newOrder.indexOf(item.key) === -1) {
        newOrder.push(item.key);
      }
    });

    const dragIndex = newOrder.indexOf(dragKey);
    const hoverIndex = newOrder.indexOf(hoverKey);

    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, dragKey);

    const newParams = newOrder.map((key, index: number) => {
      const param = items.find((item: any) => item.key === key).apiParams;
      param[sortParamName] = index;
      return param;
    })

    setOrder(newOrder);
    updateSortFunction(newParams);
  };

  const renderTabBar: TabsProps['renderTabBar'] = (tabBarProps, DefaultTabBar) => (
    <DefaultTabBar {...tabBarProps}>
      {(node) => (
        <DraggableTabNode key={node.key} index={node.key!} moveNode={moveTabNode}>
          {node}
        </DraggableTabNode>
      )}
    </DefaultTabBar>
  );

  const orderItems = [...items].sort((a, b) => {
    const orderA = order.indexOf(a.key!);
    const orderB = order.indexOf(b.key!);

    if (orderA !== -1 && orderB !== -1) {
      return orderA - orderB;
    }
    if (orderA !== -1) {
      return -1;
    }
    if (orderB !== -1) {
      return 1;
    }

    const ia = items.indexOf(a);
    const ib = items.indexOf(b);

    return ia - ib;
  });

  return (
    <Tabs renderTabBar={renderTabBar} {...props} items={orderItems} />
  );
};

export default DraggableTabs;